/**
 * Customer details — read-only definition list once intake is locked, or
 * editable form pre-lock. Internal notes are always editable (staff-only).
 * On-system styling (sm4rt-web-ui skill): def list, shared Chip, .ci-btn /
 * .ci-textbtn buttons. Text inputs stay MUI (themed via theme.ts §16).
 */

import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { IconLock, IconPencil } from '@tabler/icons-react';
import { CHECK_IN_HIGH_VALUE_THRESHOLD } from '@techgitter/contracts';
import type { CheckInSession, CheckInSessionUpdate } from '@techgitter/contracts';

import { patchCheckInSession } from '../../../services/checkInApi';
import { Chip } from '../../chip';
import { CustomerLookupAutocomplete } from '../CustomerLookupAutocomplete';
import { nameParts, splitFullName } from '../customerNameParts';
import { isPreLock } from './sessionDetailHelpers';

export interface CheckInCustomerDetailsSectionProps {
  session: CheckInSession;
  onPatched: (updated: CheckInSession) => void;
}

export const CheckInCustomerDetailsSection: React.FC<CheckInCustomerDetailsSectionProps> = ({
  session,
  onPatched,
}) => {
  const locked = !isPreLock(session);

  // Form state. For pre-lock sessions the form is the default view, so we
  // seed `draft` from the session so the inputs are populated. We re-seed
  // whenever the session id (or its modifiedon) changes so external updates
  // flow into the form when the user isn't actively editing.
  const [editing, setEditing] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [draft, setDraft] = useState<CheckInSessionUpdate>(() => sessionToDraft(session));
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (editing || editingNotes) return;
    setDraft(sessionToDraft(session));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, session.updatedAt]);

  const saveEdit = async () => {
    setSavingDraft(true);
    setError(null);
    try {
      const updated = await patchCheckInSession(session.id, draft);
      onPatched(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingDraft(false);
    }
  };

  const saveNotes = async () => {
    setSavingDraft(true);
    setError(null);
    try {
      const updated = await patchCheckInSession(session.id, { internalNotes: draft.internalNotes ?? '' });
      onPatched(updated);
      setEditingNotes(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingDraft(false);
    }
  };

  if (locked && !editing) {
    return (
      <div className="ci-section">
        <div className="ci-chips">
          <Chip tone="neutral">
            <IconLock size={12} stroke={1.5} />
            Customer details locked
          </Chip>
        </div>
        <div className="ci-def">
          <span className="ci-def__label">Name</span>
          <span className="ci-def__value">{session.customerName || '—'}</span>
          <span className="ci-def__label">Email</span>
          <span className="ci-def__value">{session.customerEmail || '—'}</span>
          <span className="ci-def__label">Phone</span>
          <span className="ci-def__value">{session.customerPhone || '—'}</span>
          <span className="ci-def__label">Business</span>
          <span className="ci-def__value">{session.businessName || '—'}</span>
          <span className="ci-def__label">Customer Notes</span>
          <span className="ci-def__value">{session.customerNotes || '—'}</span>
          {/*
            The customer's NCAT declaration. Shown as a warning chip rather than
            plain text when set, because it is a constraint on what may be done
            with the device later: the check-in terms promise no disposal without
            a tribunal order once it is declared, and whoever eventually deals
            with an uncollected item needs to see it without reading every field.

            Read-only here. The terms turn on the customer telling us at
            check-in, so it is theirs to set through the check-in flow — staff
            editing it would be recording a declaration on their behalf.
          */}
          <span className="ci-def__label">High Value</span>
          <span className="ci-def__value">
            {session.highValueDeclared ? (
              <Chip tone="warning">
                Declared {formatThreshold(CHECK_IN_HIGH_VALUE_THRESHOLD)}+ — no disposal
                without an NCAT order
              </Chip>
            ) : (
              'Not declared'
            )}
          </span>
        </div>

        <InternalNotesBlock
          session={session}
          editing={editingNotes}
          draftValue={draft.internalNotes}
          savingDraft={savingDraft}
          error={error}
          onBegin={() => {
            setDraft({ internalNotes: session.internalNotes ?? '' });
            setEditingNotes(true);
            setError(null);
          }}
          onChange={value => setDraft({ ...draft, internalNotes: value })}
          onSave={saveNotes}
          onCancel={() => setEditingNotes(false)}
        />
      </div>
    );
  }

  // Edit mode — pre-lock fields editable
  return (
    <div className="ci-form">
      {error && <div className="ci-dialog__error">{error}</div>}
      <CustomerLookupAutocomplete
        onPick={c => {
          setDraft({
            ...draft,
            // The lookup index carries only a joined name, so it is split
            // here. Reception sees the result in the two fields below and can
            // correct it before saving.
            ...splitFullName(c.fullName || c.displayName || ''),
            customerEmail: c.email ?? '',
            customerPhone: c.mobile ?? c.phone ?? '',
            businessName: c.businessName ?? '',
            repairShoprCustomerId: c.repairShoprCustomerId || null,
          });
        }}
      />
      <TextField
        label="First Name"
        size="small"
        value={draft.customerFirstName ?? ''}
        onChange={e => setDraft({ ...draft, customerFirstName: e.target.value })}
        inputProps={{ autoComplete: 'given-name' }}
        fullWidth
      />
      <TextField
        label="Last Name"
        size="small"
        value={draft.customerLastName ?? ''}
        onChange={e => setDraft({ ...draft, customerLastName: e.target.value })}
        inputProps={{ autoComplete: 'family-name' }}
        fullWidth
      />
      <TextField
        label="Email"
        size="small"
        value={draft.customerEmail ?? ''}
        onChange={e => setDraft({ ...draft, customerEmail: e.target.value })}
        inputProps={{ inputMode: 'email', autoComplete: 'email' }}
        fullWidth
      />
      <TextField
        label="Phone"
        size="small"
        value={draft.customerPhone ?? ''}
        onChange={e => setDraft({ ...draft, customerPhone: e.target.value })}
        inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
        fullWidth
      />
      <TextField
        label="Business Name"
        size="small"
        value={draft.businessName ?? ''}
        onChange={e => setDraft({ ...draft, businessName: e.target.value })}
        fullWidth
      />
      <TextField
        label="Customer Notes (customer-visible)"
        size="small"
        value={draft.customerNotes ?? ''}
        onChange={e => setDraft({ ...draft, customerNotes: e.target.value })}
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
      />
      <TextField
        label="Internal Notes (staff-only)"
        size="small"
        value={draft.internalNotes ?? session.internalNotes ?? ''}
        onChange={e => setDraft({ ...draft, internalNotes: e.target.value })}
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
      />
      <div className="ci-form__actions">
        <button type="button" className="ci-textbtn" onClick={() => setEditing(false)} disabled={savingDraft}>
          Cancel
        </button>
        <button type="button" className="ci-btn ci-btn--primary ci-btn--sm" onClick={saveEdit} disabled={savingDraft}>
          {savingDraft ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

interface InternalNotesBlockProps {
  session: CheckInSession;
  editing: boolean;
  draftValue: string | null | undefined;
  savingDraft: boolean;
  error: string | null;
  onBegin: () => void;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const InternalNotesBlock: React.FC<InternalNotesBlockProps> = ({
  session,
  editing,
  draftValue,
  savingDraft,
  error,
  onBegin,
  onChange,
  onSave,
  onCancel,
}) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span className="ci-subhead" style={{ flex: 1 }}>Internal Notes (staff-only)</span>
      {!editing && (
        <button type="button" className="ci-textbtn ci-textbtn--accent" onClick={onBegin}>
          <IconPencil size={13} stroke={1.5} />
          Edit
        </button>
      )}
    </div>
    {error && editing && <div className="ci-dialog__error" style={{ marginBottom: 8 }}>{error}</div>}
    {editing ? (
      <div className="ci-form">
        <TextField
          size="small"
          value={draftValue ?? ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
        />
        <div className="ci-form__actions">
          <button type="button" className="ci-textbtn" onClick={onCancel} disabled={savingDraft}>
            Cancel
          </button>
          <button type="button" className="ci-btn ci-btn--primary ci-btn--sm" onClick={onSave} disabled={savingDraft}>
            {savingDraft ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    ) : (
      <div className="ci-def__value" style={{ whiteSpace: 'pre-wrap' }}>
        {session.internalNotes || '—'}
      </div>
    )}
  </div>
);

function sessionToDraft(session: CheckInSession): CheckInSessionUpdate {
  return {
    // The parts, not the derived `customerName` — sending that back would let
    // a stale join overwrite the parts it is supposed to be built from.
    ...nameParts(session.customerFirstName, session.customerLastName, session.customerName),
    customerEmail: session.customerEmail,
    customerPhone: session.customerPhone,
    businessName: session.businessName,
    customerNotes: session.customerNotes,
    internalNotes: session.internalNotes,
    repairShoprCustomerId: session.repairShoprCustomerId,
  };
}


/** "$20,000" from 20000 — en-AU, no cents. */
const formatThreshold = (value: number): string =>
  value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
